import type { ReactNode } from 'react'
import { ArrowLeft20Regular, ArrowRight20Regular } from '@fluentui/react-icons'
import { useScrollSpy } from '../lib/hooks'
import { Reveal } from './Reveal'

export function PageHero({
  kicker,
  title,
  lead,
  specs,
}: {
  kicker: string
  title: string
  lead: string
  specs: [string, string][]
}) {
  return (
    <section className="page-hero grid-bg">
      <div className="shell">
        <Reveal variant="fade" className="back-link">
          <a href="/">
            <ArrowLeft20Regular />
            Overview
          </a>
        </Reveal>
        <Reveal as="p" variant="fade" className="kicker" index={1}>
          {kicker}
        </Reveal>
        <h1 className="display-1">
          <Reveal as="span" variant="mask" index={2}>
            {title}
          </Reveal>
        </h1>
        <Reveal as="p" variant="up" className="lead" index={3}>
          {lead}
        </Reveal>
        <Reveal as="dl" variant="up" className="spec-strip mono" index={4}>
          {specs.map(([term, value]) => (
            <div key={term}>
              <dt>{term}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </Reveal>
      </div>
    </section>
  )
}

export function SubNav({ items }: { items: { id: string; label: string }[] }) {
  const active = useScrollSpy(
    items.map(item => item.id),
    160,
  )

  return (
    <nav className="sub-nav" aria-label="On this page">
      <div className="shell">
        {items.map((item, index) => (
          <a key={item.id} href={`#${item.id}`} className={active === item.id ? 'is-active' : undefined}>
            <span className="mono">0{index + 1}</span>
            {item.label}
          </a>
        ))}
      </div>
    </nav>
  )
}

export function NextPage({ href, label, title }: { href: string; label: string; title: string }) {
  return (
    <Reveal variant="up" className="next-page">
      <a href={href}>
        <span className="mono">{label}</span>
        <b className="display-3">{title}</b>
        <ArrowRight20Regular />
      </a>
    </Reveal>
  )
}

export function DetailSection({
  id,
  index,
  title,
  lead,
  children,
  tinted,
}: {
  id: string
  index: string
  title: string
  lead?: string
  children: ReactNode
  tinted?: boolean
}) {
  return (
    <section id={id} className={tinted ? 'band tinted' : 'band'}>
      <div className="shell">
        <div className="section-head">
          <Reveal as="span" variant="fade" className="section-index mono">
            §&nbsp;{index}
          </Reveal>
          <div>
            <Reveal as="h2" variant="mask" className="display-2">
              {title}
            </Reveal>
            {lead && (
              <Reveal as="p" variant="up" className="lead" index={1}>
                {lead}
              </Reveal>
            )}
          </div>
        </div>
        {children}
      </div>
    </section>
  )
}
